import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import PasswordStrengthChecker, { meetsPolicy } from '../components/PasswordStrengthChecker';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff } from 'lucide-react';

export default function ChangePasswordPage() {
  const { user, isClient } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const policyMet = meetsPolicy(newPassword);
  const canSubmit = currentPassword && policyMet && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        const msg = detail.map(d => d.msg).join('; ');
        toast.error(msg);
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Staff users don't have local passwords
  if (!isClient) {
    return (
      <div>
        <div className="page-header">
          <h1>Change password</h1>
        </div>
        <div className="card" style={{ maxWidth: 480, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <Lock size={24} style={{ marginBottom: 12 }} />
          <p>Staff accounts use Microsoft 365 SSO for authentication.</p>
          <p style={{ fontSize: 13 }}>
            To change your password, go to{' '}
            <a href="https://myaccount.microsoft.com/" target="_blank" rel="noreferrer"
              style={{ color: 'var(--color-primary)' }}>
              myaccount.microsoft.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Change password</h1>
        <p>Update your account password. A strong password is required.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ maxWidth: 480 }}>
          {/* Current password */}
          <div className="form-group">
            <label className="form-label">Current password <span className="required">*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrent ? 'text' : 'password'}
                className="form-input"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-text-muted)', padding: 4,
                }}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div className="form-group">
            <label className="form-label">New password <span className="required">*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNew ? 'text' : 'password'}
                className="form-input"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter a strong password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-text-muted)', padding: 4,
                }}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <PasswordStrengthChecker password={newPassword} />
          </div>

          {/* Confirm password */}
          <div className="form-group">
            <label className="form-label">Confirm new password <span className="required">*</span></label>
            <input
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              required
              autoComplete="new-password"
              style={confirmPassword && !passwordsMatch ? { borderColor: 'var(--color-danger)' } : {}}
            />
            {confirmPassword && !passwordsMatch && (
              <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>
                Passwords do not match
              </p>
            )}
          </div>

          {/* Policy summary */}
          <div style={{
            padding: '10px 12px', borderRadius: 6, marginBottom: 16,
            background: 'var(--color-bg-subtle, #f9fafb)', fontSize: 12, color: 'var(--color-text-muted)',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Password policy</div>
            <PolicyCheck ok={newPassword.length >= 10} text="Minimum 10 characters" />
            <PolicyCheck ok={/[A-Z]/.test(newPassword)} text="At least one uppercase letter" />
            <PolicyCheck ok={/[a-z]/.test(newPassword)} text="At least one lowercase letter" />
            <PolicyCheck ok={/\d/.test(newPassword)} text="At least one number" />
            <PolicyCheck ok={/[^a-zA-Z0-9]/.test(newPassword)} text="At least one special character" />
            <PolicyCheck ok={newPassword.length > 0 && !/(.)(\1){2,}/.test(newPassword)} text="No 3+ consecutive identical characters" />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={() => navigate('/dashboard')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {submitting ? 'Changing...' : 'Change password'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function PolicyCheck({ ok, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
      <span style={{ fontSize: 11, color: ok ? '#059669' : '#d1d5db' }}>
        {ok ? '✓' : '○'}
      </span>
      <span style={{ color: ok ? '#374151' : '#9ca3af' }}>{text}</span>
    </div>
  );
}
